import { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Box,
  IconButton,
} from "@mui/material";
import { Close, Save } from "@mui/icons-material";
import theme from "../../theme/theme";
import { fetchCustomerPage } from "../../api/CustomerService";
import { fetchProjectPage } from "../../api/ProjectService";
import customerLabel from "../../utils/customerLabel";
import AsyncAutocomplete from "../common/AsyncAutocomplete";

const initialForm = {
  customer_id: "",
  project_id: "",
  subject: "",
  category: "general",
  priority: "medium",
  description: "",
};

const CreateTicketDialog = ({
  open,
  onClose,
  saving,
  onSave,
  errors = {},
}) => {
  const [form, setForm] = useState(initialForm);

  useEffect(() => {
    if (open) {
      setForm(initialForm);
    }
  }, [open]);

  // Projects scoped to the selected customer.
  const fetchCustomerProjectsPage = (page, search) =>
    fetchProjectPage(page, search, { customer_id: form.customer_id });

  const handleSave = () => {
    onSave({
      customer_id: form.customer_id,
      project_id: form.project_id || null,
      subject: form.subject.trim(),
      category: form.category,
      priority: form.priority,
      description: form.description.trim(),
    });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { borderRadius: theme.dialog.borderRadius, overflow: "hidden" },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          fontWeight: 700,
        }}
      >
        Create Support Ticket
        <IconButton onClick={onClose} size="small">
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
          <AsyncAutocomplete
            label="Customer *"
            placeholder="Search customer..."
            value={form.customer_id}
            onChange={(id) =>
              setForm({ ...form, customer_id: id, project_id: "" })
            }
            fetchPage={fetchCustomerPage}
            getOptionLabel={customerLabel}
            error={!!errors.customer_id}
            helperText={errors.customer_id?.[0]}
          />

          <AsyncAutocomplete
            label="Lift / Project (optional)"
            value={form.project_id}
            onChange={(id) => setForm({ ...form, project_id: id })}
            fetchPage={fetchCustomerProjectsPage}
            getOptionLabel={(p) => p.customer_name || `Lift #${p.id}`}
            refreshKey={form.customer_id}
            disabled={!form.customer_id}
            error={!!errors.project_id}
            helperText={errors.project_id?.[0]}
          />

          <TextField
            size="small"
            label="Subject *"
            value={form.subject}
            onChange={(e) => setForm({ ...form, subject: e.target.value })}
            error={!!errors.subject}
            helperText={errors.subject?.[0]}
          />

          <Box sx={{ display: "flex", gap: 2 }}>
            <TextField
              select
              size="small"
              label="Category *"
              value={form.category}
              onChange={(e) => setForm({ ...form, category: e.target.value })}
              error={!!errors.category}
              helperText={errors.category?.[0]}
              sx={{ flex: 1 }}
            >
              <MenuItem value="maintenance">Maintenance</MenuItem>
              <MenuItem value="breakdown">Breakdown</MenuItem>
              <MenuItem value="billing">Billing</MenuItem>
              <MenuItem value="general">General</MenuItem>
            </TextField>

            <TextField
              select
              size="small"
              label="Priority *"
              value={form.priority}
              onChange={(e) => setForm({ ...form, priority: e.target.value })}
              error={!!errors.priority}
              helperText={errors.priority?.[0]}
              sx={{ flex: 1 }}
            >
              <MenuItem value="low">Low</MenuItem>
              <MenuItem value="medium">Medium</MenuItem>
              <MenuItem value="high">High</MenuItem>
              <MenuItem value="urgent">Urgent</MenuItem>
            </TextField>
          </Box>

          <TextField
            size="small"
            label="Description *"
            multiline
            minRows={4}
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            error={!!errors.description}
            helperText={errors.description?.[0]}
          />
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        <Button
          onClick={onClose}
          disabled={saving}
          variant="outlined"
          sx={{
            borderRadius: theme.button.borderRadius,
            textTransform: "none",
            fontWeight: 600,
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          disabled={
            saving ||
            !form.customer_id ||
            !form.subject.trim() ||
            !form.description.trim()
          }
          variant="contained"
          startIcon={<Save />}
          sx={{
            borderRadius: theme.button.borderRadius,
            textTransform: "none",
            fontWeight: 600,
            backgroundColor: theme.palette.primary.main,
          }}
        >
          {saving ? "Creating..." : "Create Ticket"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateTicketDialog;
